import CompactEmptyState from 'components/EmptyState/CompactEmptyState';
import SlideOverBody from 'components/SlideOver/component/body';
import SlideOverHeader from 'components/SlideOver/component/header';
import useSlideOver from 'components/SlideOver/useSlideOver';
import PillTabs from 'components/tabs/PillTabs';
import {
  addDays,
  clockLabel,
  dayLabel,
  fromIsoDay,
  isoDay,
} from 'helper/timeLabels';
import useHomeText from 'hooks/useHomeText';
import useMentions from 'hooks/useMentions';
import useTaskPanels from 'hooks/useTaskPanels';
import { useState } from 'react';
import { LIST_GROUP_HEADER_CLASS } from 'utils/surfaceClasses';
import MentionItem, { MentionSkeleton } from './MentionItem';

export const MENTIONS_PANEL = 'mentions';

const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);

// The mentions under the head of their day, in the order they come.
const byDay = (mentions) =>
  mentions.reduce((groups, mention) => {
    const day = isoDay(new Date(mention.createdAt));
    const last = groups[groups.length - 1];
    if (last?.day === day) last.mentions.push(mention);
    else groups.push({ day, mentions: [mention] });
    return groups;
  }, []);

/**
 * All the comments that name the user, opened from « Voir tout » on the home
 * page: all of them or the unread ones only, by day, the latest first. One
 * opens its task on its comments, in the place of this panel.
 */
const MentionsPanel = () => {
  const { text } = useHomeText();
  const { toggleSlideOver } = useSlideOver();
  const { openTask } = useTaskPanels();
  const { mentions, failed, me, reload } = useMentions();
  const [filter, setFilter] = useState('all');

  const now = new Date();
  const unread = (mentions ?? []).filter((mention) => mention.unread);
  const shown = filter === 'unread' ? unread : (mentions ?? []);
  const dayHead = (day) => {
    if (day === isoDay(now)) return text('tasks.today');
    if (day === isoDay(addDays(now, -1))) return text('tasks.yesterday');
    return capitalize(dayLabel(fromIsoDay(day)));
  };

  return (
    <>
      <SlideOverHeader
        hide={() => toggleSlideOver()}
        closeLabel={text('mentions.close')}
      >
        {text('mentions.title')}
      </SlideOverHeader>
      <div className="flex-shrink-0 px-4 pb-3 md:px-6">
        <PillTabs
          activeTab={filter}
          onChange={setFilter}
          tabs={[
            {
              id: 'all',
              label: text('mentions.filterAll'),
              count: mentions?.length,
            },
            {
              id: 'unread',
              label: text('mentions.filterUnread'),
              count: mentions ? unread.length : undefined,
            },
          ]}
        />
      </div>
      {/* Edge to edge, for the heads of the days to stick across it. */}
      <SlideOverBody className="flex flex-col border-t border-gray-100 !p-0">
        {failed && (
          <CompactEmptyState
            compact
            title={text('mentions.loadError')}
            action={{ label: text('mentions.retry'), onClick: reload }}
          />
        )}
        {!failed && !mentions && (
          <div className="px-2 py-3 md:px-4">
            <MentionSkeleton rows={4} />
          </div>
        )}
        {!failed && mentions && !shown.length && (
          <CompactEmptyState
            compact
            glyph={filter === 'unread' ? 'badgeCheck' : 'inbox'}
            title={text(
              filter === 'unread'
                ? 'mentions.emptyUnreadTitle'
                : 'mentions.emptyTitle',
            )}
            description={text(
              filter === 'unread'
                ? 'mentions.emptyUnreadText'
                : 'mentions.emptyText',
            )}
          />
        )}
        {!failed &&
          byDay(shown).map((group) => (
            <section key={group.day}>
              <p
                className={`sticky top-0 z-10 border-b border-gray-100 md:px-6 ${LIST_GROUP_HEADER_CLASS}`}
              >
                {dayHead(group.day)}
              </p>
              <ul className="flex flex-col px-2 py-1 md:px-4">
                {group.mentions.map((mention) => (
                  <li key={mention.id}>
                    <MentionItem
                      mention={mention}
                      me={me}
                      when={clockLabel(new Date(mention.createdAt))}
                      onOpen={() =>
                        openTask(mention.task.id, { tab: 'comments' })
                      }
                    />
                  </li>
                ))}
              </ul>
            </section>
          ))}
      </SlideOverBody>
    </>
  );
};

export default MentionsPanel;
